import { postReferralsCje, type PostReferralsCjePayload } from '@/api/post-referrals-cje'
import { useCookies } from '@/composables/cookies'
import { useLogger } from '@booksy-com/vue-tools'

import {
  CJ_AID_FALLBACK_QUERY_PARAM,
  CJ_AID_QUERY_PARAM,
  CJ_COOKIE_NAME,
  CJ_PID_FALLBACK_QUERY_PARAM,
  CJ_PID_QUERY_PARAM,
} from './consts'
import {
  getCjEventIdFromSearch,
  normalizeCjEventId,
  normalizeCjFieldValue,
} from './index'

const logger = useLogger('cj-affiliate:persist')

function getCjFieldFromSearch (search: string, ...paramNames: string[]): string | null {
  for (const paramName of paramNames) {
    const params = new URLSearchParams(search)

    for (const [key, value] of params.entries()) {
      if (key.toLowerCase() === paramName.toLowerCase()) {
        const normalizedValue = normalizeCjFieldValue(value)

        if (normalizedValue) {
          return normalizedValue
        }
      }
    }
  }

  return null
}

function resolveCjEventForPersist (search: string): string | null {
  const cjEventFromUrl = getCjEventIdFromSearch(search)

  if (cjEventFromUrl) {
    return cjEventFromUrl
  }

  const { getCookie } = useCookies()

  return normalizeCjEventId(getCookie(CJ_COOKIE_NAME))
}

function buildCjPersistPayload (search: string): PostReferralsCjePayload | null {
  const cjevent = resolveCjEventForPersist(search)

  if (!cjevent) {
    return null
  }

  const payload: PostReferralsCjePayload = { cjevent }

  const aid = getCjFieldFromSearch(search, CJ_AID_QUERY_PARAM, CJ_AID_FALLBACK_QUERY_PARAM)

  if (aid) {
    payload.aid = aid
  }

  const pid = getCjFieldFromSearch(search, CJ_PID_QUERY_PARAM, CJ_PID_FALLBACK_QUERY_PARAM)

  if (pid) {
    payload.pid = pid
  }

  return payload
}

export async function persistCjEvent (payload: PostReferralsCjePayload): Promise<void> {
  const normalizedCjEvent = normalizeCjEventId(payload.cjevent)

  if (!normalizedCjEvent) {
    return
  }

  const data: PostReferralsCjePayload = { cjevent: normalizedCjEvent }

  const aid = normalizeCjFieldValue(payload.aid)

  if (aid) {
    data.aid = aid
  }

  const pid = normalizeCjFieldValue(payload.pid)

  if (pid) {
    data.pid = pid
  }

  await postReferralsCje(data)
}

export async function tryPersistCjEvent (search?: string): Promise<void> {
  const searchValue = search ?? (typeof window !== 'undefined' ? window.location.search : '')
  const payload = buildCjPersistPayload(searchValue)

  if (!payload) {
    return
  }

  try {
    await persistCjEvent(payload)
  } catch (error) {
    logger.warn('Failed to persist CJ event cookie', error)
  }
}

export function initCjAffiliateAttribution (): void {
  void tryPersistCjEvent()
}
